import { Box, useMediaQuery, useTheme } from "@mui/material";
import { IconList, IconMap } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UpcomingGamesList } from "../../../components/game";
import GameLocationMap from "../../../components/maps/GameLocationMap";
import TabView from "../../../components/misc/TabView";
import { SEO } from "../../../components/seo";
import Button from "../../../components/ui/Button";
import Text from "../../../components/ui/Text";
import { ROUTES } from "../../../constants/routes";
import { PAGE_SEO } from "../../../constants/seo";
import { useUpcomingGames } from "./hooks";
import { ErrorState, LoadingState } from "./parts";

const UpcomingGames = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [activeTab, setActiveTab] = useState("list");

  const { games, loading, error, refreshGames } = useUpcomingGames();

  const tabs = [
    {
      label: "List View",
      value: "list",
      icon: <IconList size={20} />,
    },
    {
      label: "Map View",
      value: "map",
      icon: <IconMap size={20} />,
    },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleGameClick = (game) => {
    navigate(ROUTES.GAME_DETAILS.replace(":gameId", game.id));
  };

  // Show loading state
  if (loading) {
    return (
      <Box maxWidth="xl" sx={{ mt: 2 }}>
        <SEO
          title={PAGE_SEO.GAMES.title}
          description={PAGE_SEO.GAMES.description}
          keywords={PAGE_SEO.GAMES.keywords}
        />
        <LoadingState />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box maxWidth="xl" sx={{ mt: 2 }}>
        <SEO
          title={PAGE_SEO.GAMES.title}
          description={PAGE_SEO.GAMES.description}
          keywords={PAGE_SEO.GAMES.keywords}
        />
        <ErrorState error={error} onRetry={refreshGames} />
      </Box>
    );
  }

  // Desktop layout with split-screen view
  if (isDesktop) {
    return (
      <>
        <SEO
          title={PAGE_SEO.GAMES.title}
          description={PAGE_SEO.GAMES.description}
          keywords={PAGE_SEO.GAMES.keywords}
        />
        {/* Page Header with Create Game Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Text variant="pageHeader">Upcoming games</Text>
          <Button
            variant="primary"
            onClick={() => navigate("/create-game")}
            sx={{
              backgroundColor: "#1890FF",
              color: "white",
              "&:hover": {
                backgroundColor: "#1677FF",
              },
              width: "auto",
              whiteSpace: "nowrap",
            }}
          >
            Create a game
          </Button>
        </Box>

        {/* Split-screen layout */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "4fr 6fr",
              lg: "320px 1fr",
            },
            gap: 3,
          }}
        >
          {/* Left side - Map View */}
          <Box>
            <GameLocationMap games={games} onGameClick={handleGameClick} />
          </Box>

          {/* Right side - List View */}
          <Box
            sx={{
              overflow: "auto",
              pr: 1,
              pb: 2,
              height: "calc(100vh - 226px)",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#a8a8a8",
              },
            }}
          >
            <UpcomingGamesList games={games} nextSevenDays />
          </Box>
        </Box>
      </>
    );
  }

  // Mobile layout (original tabbed view)
  return (
    <Box maxWidth="xl" sx={{ pt: 2, pb: 4, mx: -2 }}>
      <SEO
        title={PAGE_SEO.GAMES.title}
        description={PAGE_SEO.GAMES.description}
        keywords={PAGE_SEO.GAMES.keywords}
      />
      {/* Page Header */}
      <Text variant="pageHeader" sx={{ mb: 4, ml: 2 }}>
        Upcoming games
      </Text>

      <TabView
        sx={{ ml: 2 }}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      >
        {activeTab === "list" ? (
          <Box sx={{ p: 2 }}>
            <UpcomingGamesList
              games={games}
              nextSevenDays
              onGameClick={handleGameClick}
            />
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            <GameLocationMap games={games} onGameClick={handleGameClick} />
          </Box>
        )}
      </TabView>
    </Box>
  );
};

export default UpcomingGames;
